// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { localize } from '@ta/common.i18n';
import classNames from '@ta/common.classnames';
import { Icon } from '@ta/styleguide.icon';
import { Link } from '@ta/common.routing';
import { featureIsEnabled } from '@ta/common.features';
import { TripsLink } from '@ta/trips.states';
import { ClickTracker, type TrackingEventAJAXProps } from '@ta/common.tracking';
import { HideOnReadonly } from '@ta/failover.readonly';
import PostButtonCollapsedMenu from '@ta/brand.mobile-global-nav-content-collect';

import { t4bNavPages } from './T4BNav';
import styles from './ProfileSubLinks.less';

type ExternalProps = {|
  /** If there are unread messages then the inbox link should display a comment icon with a jewel */
  displayInboxIcon: boolean,
  /** If the current user is an owner then we need to display a management center in the sideBar */
  isOwner: boolean,
  /** This function is used to set Uploader Modal's modalname and pid which are used to show coresponding uploader modal  */
  setModalInfo: (name: string, pid: number) => void,
|};

type Props = {|
  ...ExternalProps,
  /** Used for tracking purposes */
  servletName: string,
  /** Used for tracking purposes */
  uid: string | null,
  /** Determine if this page is a T4B page */
  extraContentCollectionPredicates: boolean,
|};

/** Returns the props for the profile ClickTrackers */
const getClickTrackingProps = (context: string, servletName: string): TrackingEventAJAXProps => ({
  context,
  action: 'click',
  module: `UtilityNav_${servletName}`,
});

/**
 * These are the four links underneath the ProfileDataCell ("My trips", "Recently Viewed", "Bookings", & "inbox") that
 * appear in the sideBar (hamburger) slide-out menu on mobile. If the user is an owner the first link it to the management center
 */

const ProfileSubLinks = (props: Props) => {
  const jewelClass = classNames('ui_jewel', styles.jewel);
  const isAddGlobalNavCTA =
    !props.extraContentCollectionPredicates && featureIsEnabled('add_cta_to_global_nav');

  const ownerLink = props.isOwner ? (
    <li className={styles.listItem}>
      <ClickTracker {...getClickTrackingProps('UserProfile_ManagementCenter', props.servletName)}>
        <Link to={{ page: 'Owners' }} className={styles.profileSubLink}>
          {/* My business */}
          {localize('global_nav_profile_my_business')}
        </Link>
      </ClickTracker>
    </li>
  ) : null;

  const inboxIconAndJewel = props.displayInboxIcon ? (
    <div className={styles.inboxIconAndJewel}>
      <Icon name="comments" />
      <div className={jewelClass} />
    </div>
  ) : null;

  return (
    <ul>
      {ownerLink}
      {isAddGlobalNavCTA && (
        <li className={styles.listItem}>
          <PostButtonCollapsedMenu setModalInfo={props.setModalInfo} isOwner={props.isOwner} />
        </li>
      )}
      <li className={styles.listItem}>
        <TripsLink
          loggedInElement={
            <ClickTracker {...getClickTrackingProps('My_trips', props.servletName)}>
              <Link href="/Trips" className={styles.profileSubLink}>
                {/* Trips */}
                {localize('trips_product_name')}
              </Link>
            </ClickTracker>
          }
          loggedOutElement={open => (
            <ClickTracker {...getClickTrackingProps('My_trips', props.servletName)}>
              <Link className={styles.profileSubLink} onClick={open}>
                {/* Trips */}
                {localize('trips_product_name')}
              </Link>
            </ClickTracker>
          )}
        />
      </li>
      <li className={styles.listItem}>
        <ClickTracker
          dstServlet="MobileSaves"
          {...getClickTrackingProps('Recently_viewed', props.servletName)}
        >
          <Link href="/Recent" className={styles.profileSubLink}>
            {/* Recently viewed */}
            {localize('rebrand_recently_viewed_shelf_title')}
          </Link>
        </ClickTracker>
      </li>
      {featureIsEnabled('member_profile_bookings') && (
        <li className={styles.listItem}>
          <ClickTracker
            {...getClickTrackingProps('UserProfile_ManagementCenter', props.servletName)}
          >
            <Link href="/Bookings" className={styles.profileSubLink}>
              {/* Bookings */}
              {localize('my_bookings_headtext_fffff9b3')}
            </Link>
          </ClickTracker>
        </li>
      )}
      {featureIsEnabled('unified_inbox_v2') && (
        <HideOnReadonly>
          <li className={styles.listItem}>
            <ClickTracker {...getClickTrackingProps('UserProfile_inbox', props.servletName)}>
              <Link href="/Inbox" className={styles.profileSubLink}>
                {/* Inbox */}
                {localize('inbox_title')}
                {inboxIconAndJewel}
              </Link>
            </ClickTracker>
          </li>
        </HideOnReadonly>
      )}
    </ul>
  );
};

/** Connect to the redux store so we can get the servletName and pass it as props */
export default connect<Props, ExternalProps, _, _, _, _>(state => ({
  servletName: state.meta.initialServletName,
  extraContentCollectionPredicates: t4bNavPages.includes(state.route.page),
  uid: state.tracking.uid || '',
}))(ProfileSubLinks);
